import QRCodeStyling from "qr-code-styling";
import "@fontsource/urbanist/400.css";
import "@fontsource/urbanist/700.css";
import qrLogo from "../assets/tiply_logo_v4.png";
import paymentMethods from "../assets/apple-google-pay.png";

export const TIPPING_CARD_DIMENSIONS = Object.freeze({
    widthMm: 56,
    heightMm: 96,
    trimWidthMm: 50,
    trimHeightMm: 90,
    bleedMm: 3,
    safeInsetMm: 6,
    safeWidthMm: 44,
    safeHeightMm: 84,
    pixelsPerMm: 12,
    widthPx: 672,
    heightPx: 1152,
});

const CARD_COLORS = Object.freeze({
    navy: "#18182e",
    mint: "#31e2c2",
    white: "#ffffff",
});

const cleanName = (value) => String(value || "").trim().replace(/\s+/g, " ");

export const getTippingCardHeadline = ({ targetName, isTeam = false } = {}) => {
    if (isTeam) return "Tip Our Team";
    const name = cleanName(targetName);
    return name ? `Tip ${name}` : "Tip Your Server";
};

export const buildTippingCardFilename = ({ targetName, isTeam = false, extension = "pdf" } = {}) => {
    const subject = isTeam ? "team" : cleanName(targetName) || "your-server";
    const safeSubject = subject
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "your-server";
    const safeExtension = String(extension || "pdf").toLowerCase().replace(/[^a-z]/g, "") || "pdf";
    return `tiply-${safeSubject}-qr-card.${safeExtension}`;
};

const loadImage = (source) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("A card image could not be loaded."));
    image.src = source;
});

const canvasToBlobInternal = (canvas, type, quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("The tipping card could not be exported."));
    }, type, quality);
});

export const canvasToBlob = (canvas, type = "image/png", quality) => (
    canvasToBlobInternal(canvas, type, quality)
);

const splitOversizedWord = (context, word, maxWidth) => {
    const parts = [];
    let current = "";

    Array.from(word).forEach((character) => {
        const candidate = `${current}${character}`;
        if (current && context.measureText(candidate).width > maxWidth) {
            parts.push(current);
            current = character;
        } else {
            current = candidate;
        }
    });

    if (current) parts.push(current);
    return parts;
};

const wrapText = (context, text, maxWidth) => {
    const tokens = cleanName(text).split(" ").flatMap((word) => (
        context.measureText(word).width > maxWidth
            ? splitOversizedWord(context, word, maxWidth)
            : [word]
    ));
    const lines = [];
    let current = "";

    tokens.forEach((token) => {
        const candidate = current ? `${current} ${token}` : token;
        if (current && context.measureText(candidate).width > maxWidth) {
            lines.push(current);
            current = token;
        } else {
            current = candidate;
        }
    });

    if (current) lines.push(current);
    return lines;
};

const splitIntoBalancedLines = (context, text) => {
    const characters = Array.from(cleanName(text));
    if (characters.length < 2) return [text];

    let bestLines = [text];
    let bestWidth = Number.POSITIVE_INFINITY;
    for (let splitIndex = 1; splitIndex < characters.length; splitIndex += 1) {
        const firstLine = characters.slice(0, splitIndex).join("").trim();
        const secondLine = characters.slice(splitIndex).join("").trim();
        if (!firstLine || !secondLine) continue;
        const widestLine = Math.max(
            context.measureText(firstLine).width,
            context.measureText(secondLine).width
        );
        if (widestLine < bestWidth) {
            bestLines = [firstLine, secondLine];
            bestWidth = widestLine;
        }
    }
    return bestLines;
};

export const fitHeadline = (context, headline, {
    maxWidth = 528,
    maxHeight = 120,
    maxLines = 2,
    startFontSize = 68,
    minFontSize = 24,
} = {}) => {
    for (let fontSize = startFontSize; fontSize >= minFontSize; fontSize -= 2) {
        context.font = `700 ${fontSize}px Urbanist, Arial, sans-serif`;
        const lines = wrapText(context, headline, maxWidth);
        const lineHeight = Math.round(fontSize * 1.04);
        if (lines.length <= maxLines && lines.length * lineHeight <= maxHeight) {
            return { fontSize, lineHeight, lines };
        }
    }

    context.font = `700 ${minFontSize}px Urbanist, Arial, sans-serif`;
    const lines = splitIntoBalancedLines(context, headline).slice(0, maxLines);
    const widestLine = Math.max(...lines.map((line) => context.measureText(line).width));
    const fittedFontSize = Math.max(8, Math.floor(minFontSize * Math.min(1, maxWidth / widestLine)));
    return {
        fontSize: fittedFontSize,
        lineHeight: Math.round(fittedFontSize * 1.04),
        lines,
    };
};

const waitForCardFonts = async () => {
    if (!document.fonts?.load) return;
    await Promise.all([
        document.fonts.load("400 32px Urbanist"),
        document.fonts.load("700 68px Urbanist"),
    ]);
};

const createQrImage = async (tipLink) => {
    const qrCode = new QRCodeStyling({
        width: 1000,
        height: 1000,
        margin: 96,
        data: tipLink,
        image: qrLogo,
        qrOptions: { errorCorrectionLevel: "H" },
        imageOptions: {
            crossOrigin: "anonymous",
            hideBackgroundDots: true,
            margin: 6,
            imageSize: 0.32,
        },
        dotsOptions: { color: "#000000", type: "square" },
        cornersSquareOptions: { color: "#000000", type: "square" },
        cornersDotOptions: { color: "#000000", type: "square" },
        backgroundOptions: { color: CARD_COLORS.white },
    });
    const qrBlob = await qrCode.getRawData("png");
    if (!qrBlob) throw new Error("The QR code could not be generated.");

    const qrUrl = URL.createObjectURL(qrBlob);
    try {
        return await loadImage(qrUrl);
    } finally {
        URL.revokeObjectURL(qrUrl);
    }
};

const measureLetterSpacedText = (context, text, spacing) => {
    const characters = Array.from(text);
    const textWidth = characters.reduce((sum, character) => sum + context.measureText(character).width, 0);
    return textWidth + Math.max(0, characters.length - 1) * spacing;
};

const drawLetterSpacedText = (context, text, x, y, spacing) => {
    let cursor = x;
    Array.from(text).forEach((character, index, characters) => {
        context.fillText(character, cursor, y);
        cursor += context.measureText(character).width;
        if (index < characters.length - 1) cursor += spacing;
    });
};

const drawPoweredBy = (context, centerX, y) => {
    const prefix = "powered by ";
    const brand = "TIPLY";
    const brandSpacing = 2.4;

    context.textAlign = "left";
    context.textBaseline = "middle";
    context.font = "400 24px Urbanist, Arial, sans-serif";
    const prefixWidth = context.measureText(prefix).width;
    context.font = "700 25px Urbanist, Arial, sans-serif";
    const brandWidth = measureLetterSpacedText(context, brand, brandSpacing);
    let cursor = centerX - (prefixWidth + brandWidth) / 2;

    context.font = "400 24px Urbanist, Arial, sans-serif";
    context.fillText(prefix, cursor, y);
    cursor += prefixWidth;
    context.font = "700 25px Urbanist, Arial, sans-serif";
    drawLetterSpacedText(context, brand, cursor, y, brandSpacing);
};

export const renderTippingCard = async ({ tipLink, targetName, isTeam = false } = {}) => {
    if (!tipLink) throw new Error("A Tiply link is required to create this card.");

    await waitForCardFonts();
    const [qrImage, paymentMethodsImage] = await Promise.all([
        createQrImage(tipLink),
        loadImage(paymentMethods),
    ]);

    const canvas = document.createElement("canvas");
    canvas.width = TIPPING_CARD_DIMENSIONS.widthPx;
    canvas.height = TIPPING_CARD_DIMENSIONS.heightPx;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("This browser cannot render the tipping card.");

    context.fillStyle = CARD_COLORS.white;
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = CARD_COLORS.mint;
    context.fillRect(288, 78, 96, 8);

    context.fillStyle = CARD_COLORS.navy;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.font = "400 32px Urbanist, Arial, sans-serif";
    context.fillText("Enjoyed the service?", canvas.width / 2, 130);

    const headline = getTippingCardHeadline({ targetName, isTeam });
    const fittedHeadline = fitHeadline(context, headline);
    context.font = `700 ${fittedHeadline.fontSize}px Urbanist, Arial, sans-serif`;
    const headlineCenterY = 220;
    const headlineHeight = fittedHeadline.lines.length * fittedHeadline.lineHeight;
    const firstLineY = headlineCenterY - headlineHeight / 2 + fittedHeadline.lineHeight / 2;
    fittedHeadline.lines.forEach((line, index) => {
        context.fillText(line, canvas.width / 2, firstLineY + index * fittedHeadline.lineHeight);
    });

    context.fillStyle = CARD_COLORS.mint;
    context.fillRect(300, 280, 72, 7);

    const qrSize = 480;
    context.drawImage(qrImage, 96, 310, qrSize, qrSize);

    const paymentWidth = 348;
    const paymentHeight = paymentWidth * (paymentMethodsImage.naturalHeight / paymentMethodsImage.naturalWidth);
    context.drawImage(
        paymentMethodsImage,
        (canvas.width - paymentWidth) / 2,
        838,
        paymentWidth,
        paymentHeight
    );

    context.fillStyle = CARD_COLORS.navy;
    drawPoweredBy(context, canvas.width / 2, 1032);

    return canvas;
};

export const createTippingCardBlob = async (canvas, extension) => {
    if (!canvas) throw new Error("Create the tipping card before downloading it.");

    if (extension === "png") return canvasToBlobInternal(canvas, "image/png");
    if (extension === "jpeg") return canvasToBlobInternal(canvas, "image/jpeg", 0.96);
    if (extension !== "pdf") throw new Error("Unsupported tipping card format.");

    const { jsPDF } = await import("jspdf");
    const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [TIPPING_CARD_DIMENSIONS.widthMm, TIPPING_CARD_DIMENSIONS.heightMm],
        compress: true,
    });
    pdf.setProperties({
        title: "Tiply tipping card",
        subject: "Print-ready Tiply QR tipping card",
        author: "Tiply",
        creator: "Tiply",
    });
    pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        0,
        0,
        TIPPING_CARD_DIMENSIONS.widthMm,
        TIPPING_CARD_DIMENSIONS.heightMm,
        undefined,
        "FAST"
    );
    return pdf.output("blob");
};

export const downloadBlob = (blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};
